import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteUrl, siteTitle } from 'config/constants';

const PUBLISHED_DATE = '2021-01-01T00:00:00Z';
const MODIFIED_DATE = '2025-04-09T00:00:00Z';

const SEO = ({
  title = siteTitle,
  description = 'Technical Lead at Nagarro with 9+ years fintech experience. Expert in React/React Native, Node.js, team leadership, AI/LLM platforms.',
  keywords = 'Technical Lead, Software Engineer, React Developer, React Native, Node.js, Fintech, Team Leadership, Staff Engineer, Full Stack Developer, JavaScript, Portfolio, UAE, Nagarro, Technical Leadership, Fintech Solutions, Cross-Cultural Leadership, AI Platform Development, AI/LLM Expert, Artificial Intelligence, Machine Learning',
  image = '/social-image.png',
  url = siteUrl,
  type = 'website',
  publishedTime = PUBLISHED_DATE,
  modifiedTime = MODIFIED_DATE,
}) => {
  const baseName = 'Hashan Jayalath - Technical Lead';
  const fullTitle = title === baseName || title === siteTitle ? title : `${title} | ${baseName}`;
  const imageUrl = image.startsWith('http') ? image : `${url}${image}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Hashan Jayalath" />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />
      <meta name="language" content="en" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="copyright" content="© 2025 Hashan Jayalath" />

      <link rel="author" href="https://www.linkedin.com/in/hashanjayalath" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content="Hashan Jayalath - Technical Lead & AI/LLM Expert Portfolio" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Hashan Jayalath Portfolio" />
      <meta property="og:locale" content="en_US" />
      <meta property="article:published_time" content={publishedTime} />
      <meta property="article:modified_time" content={modifiedTime} />
      <meta property="og:updated_time" content={modifiedTime} />

      {/* Twitter/X Card (no account -- omit creator/site) */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content="Hashan Jayalath - Technical Lead & AI/LLM Expert Portfolio" />

      {/* Person Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          '@id': `${siteUrl}/#person`,
          name: 'Hashan Jayalath',
          jobTitle: 'Technical Lead',
          worksFor: {
            '@type': 'Organization',
            name: 'Nagarro',
            url: 'https://www.nagarro.com',
          },
          url: siteUrl,
          image: imageUrl,
          description: description,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': siteUrl,
          },
          sameAs: [
            'https://www.linkedin.com/in/hashanjayalath',
            'https://github.com/hashaneranda',
            'https://medium.com/@hashaneranda',
          ],
          knowsAbout: [
            'React',
            'React Native',
            'Node.js',
            'JavaScript',
            'TypeScript',
            'Technical Leadership',
            'Fintech',
            'Team Management',
            'Full Stack Development',
            'AI Platform Development',
            'Cross-Cultural Leadership',
            'UAE Banking',
            'Mobile App Development',
            'Web Development',
            'Software Architecture',
          ],
          alumniOf: {
            '@type': 'EducationalOrganization',
            name: 'University College of Dublin',
            url: 'https://www.ucd.ie',
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'UAE',
            addressRegion: 'Dubai',
          },
          email: 'hashanjayalath@yahoo.com',
          nationality: 'Sri Lankan',
          award: [
            'Technical Leadership Excellence - Nagarro',
            'Fintech Innovation Award - UAE Banking Sector',
          ],
          memberOf: {
            '@type': 'Organization',
            name: 'Nagarro Technical Leadership Team',
          },
        })}
      </script>

      {/* WebSite Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          name: 'Hashan Jayalath Portfolio',
          url: siteUrl,
          description: description,
          inLanguage: 'en',
          datePublished: PUBLISHED_DATE,
          dateModified: MODIFIED_DATE,
          copyrightYear: '2025',
          copyrightHolder: {
            '@id': `${siteUrl}/#person`,
          },
          author: {
            '@id': `${siteUrl}/#person`,
          },
          publisher: {
            '@id': `${siteUrl}/#person`,
          },
        })}
      </script>

      {/* ProfessionalService Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ProfessionalService',
          '@id': `${siteUrl}/#service`,
          name: 'Hashan Jayalath - Technical Leadership Services',
          description:
            'Technical Leadership, Fintech Consulting, and Software Development Services',
          provider: {
            '@id': `${siteUrl}/#person`,
          },
          areaServed: {
            '@type': 'Place',
            name: 'United Arab Emirates',
          },
          serviceType: [
            'Technical Leadership',
            'Fintech Consulting',
            'Software Development',
            'Team Management',
            'Cross-Cultural Leadership',
          ],
          priceRange: 'Contact for pricing',
          url: siteUrl,
          email: 'hashanjayalath@yahoo.com',
        })}
      </script>

      {/* BreadcrumbList Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: siteUrl,
            },
          ],
        })}
      </script>

      {/* PWA / App Meta */}
      <meta name="theme-color" content="#0066cc" />
      <meta name="msapplication-TileColor" content="#0066cc" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Hashan Jayalath" />
      <meta name="application-name" content="Hashan Jayalath Portfolio" />
      <meta name="format-detection" content="telephone=no" />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="preconnect" href="https://api.emailjs.com" />

      <link rel="dns-prefetch" href="//api.github.com" />
      <link rel="dns-prefetch" href="//linkedin.com" />
      <link rel="dns-prefetch" href="//medium.com" />

      <meta name="geo.region" content="AE-DU" />
      <meta name="geo.placename" content="Dubai" />
      <meta name="geo.position" content="25.2048;55.2708" />
      <meta name="ICBM" content="25.2048, 55.2708" />
    </Helmet>
  );
};

export default SEO;
